import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from "@/components/ui/button";
import { Upload, Info, FileText, Map } from "lucide-react";
import { useLanguage } from '@/contexts/LanguageContext';

// Fix for default markers in Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});

// Utility function to calculate polygon area in square meters
const calculatePolygonArea = (latlngs: L.LatLng[]): number => {
  if (latlngs.length < 3) return 0;
  let area = 0;
  const R = 6371000; // Earth's radius in meters

  for (let i = 0; i < latlngs.length; i++) {
    const j = (i + 1) % latlngs.length;
    const lat1 = latlngs[i].lat * Math.PI / 180;
    const lat2 = latlngs[j].lat * Math.PI / 180;
    const deltaLng = (latlngs[j].lng - latlngs[i].lng) * Math.PI / 180;
    area += deltaLng * (2 + Math.sin(lat1) + Math.sin(lat2));
  }
  area = Math.abs(area * R * R / 2);
  return area;
};
const formatArea = (area: number): string => {
  if (area >= 1000000) {
    return `${(area / 1000000).toFixed(2)} km²`;
  }
  return `${area.toFixed(0)} m²`;
};
const parseKMLCoordinates = (coordinatesText: string): L.LatLng[] => {
  return coordinatesText.trim().split(/\s+/).map(coord => {
    const [lng, lat] = coord.split(',').map(Number);
    return new L.LatLng(lat, lng);
  });
};
interface KMLViewerProps {
  mode?: 'generator' | 'viewer';
  onModeChange?: (mode: 'generator' | 'viewer') => void;
  showModeToggle?: boolean;
}

const KMLViewer = ({
  mode = 'viewer',
  onModeChange,
  showModeToggle = false
}: KMLViewerProps) => {
  const { t } = useLanguage();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<L.Map | null>(null);
  const kmlLayer = useRef<L.FeatureGroup>(new L.FeatureGroup());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [hasKML, setHasKML] = useState(false);
  const [totalArea, setTotalArea] = useState(0);
  const [fileName, setFileName] = useState('');
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;

    // Initialize map centered on Berlin
    const map = L.map(mapRef.current, {
      center: [52.5200, 13.4050], // Berlin, Germany coordinates
      zoom: 10,
      zoomControl: true,
      scrollWheelZoom: true,
      doubleClickZoom: true,
      touchZoom: true,
      boxZoom: false,
      dragging: true
    });

    // Add tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19
    }).addTo(map);

    // Add KML layer
    map.addLayer(kmlLayer.current);
    mapInstance.current = map;
    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, []);
  const calculateTotalArea = () => {
    const layers = kmlLayer.current.getLayers();
    let total = 0;
    layers.forEach((layer: any) => {
      if (layer instanceof L.Polygon) {
        const latlngs = layer.getLatLngs()[0] as L.LatLng[];
        total += calculatePolygonArea(latlngs);
      }
    });
    setTotalArea(total);
  };
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = e => {
      const kmlContent = e.target?.result as string;
      parseAndDisplayKML(kmlContent);
    };
    reader.readAsText(file);
  };
  const parseAndDisplayKML = (kmlContent: string) => {
    try {
      const parser = new DOMParser();
      const kmlDoc = parser.parseFromString(kmlContent, 'application/xml');

      // Clear existing layers
      kmlLayer.current.clearLayers();

      // Find all Polygon elements
      const polygons = kmlDoc.getElementsByTagName('Polygon');
      if (polygons.length === 0) {
        throw new Error('No polygons found in KML file');
      }
      let bounds: L.LatLngBounds | null = null;
      for (let i = 0; i < polygons.length; i++) {
        const polygon = polygons[i];
        const coordinatesElement = polygon.querySelector('outerBoundaryIs LinearRing coordinates');
        if (coordinatesElement) {
          const coordinatesText = coordinatesElement.textContent || '';
          const coordinates = parseKMLCoordinates(coordinatesText);
          if (coordinates.length > 0) {
            const leafletPolygon = L.polygon(coordinates, {
              color: '#2563eb',
              fillColor: '#3b82f6',
              fillOpacity: 0.3,
              weight: 3
            });
            kmlLayer.current.addLayer(leafletPolygon);
            if (!bounds) {
              bounds = leafletPolygon.getBounds();
            } else {
              bounds.extend(leafletPolygon.getBounds());
            }
          }
        }
      }
      if (bounds && mapInstance.current) {
        mapInstance.current.fitBounds(bounds);
      }
      setHasKML(true);
      calculateTotalArea();

      // Show success notification
      const notification = document.createElement('div');
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #10b981;
        color: white;
        padding: 12px 16px;
        border-radius: 6px;
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        z-index: 1000;
        font-size: 14px;
        font-weight: 500;
        max-width: 300px;
      `;
      notification.textContent = `KML file loaded successfully`;
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 4000);
    } catch (error) {
      console.error('Error parsing KML:', error);

      // Show error notification
      const notification = document.createElement('div');
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #ef4444;
        color: white;
        padding: 12px 16px;
        border-radius: 6px;
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        z-index: 1000;
        font-size: 14px;
        font-weight: 500;
        max-width: 300px;
      `;
      notification.textContent = 'Error loading KML file. Please check the file format.';
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 4000);
    }
  };
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };
  const handleReupload = () => {
    setHasKML(false);
    setTotalArea(0);
    setFileName('');
    kmlLayer.current.clearLayers();
    fileInputRef.current?.click();
  };
  return (
    <div className="w-full max-w-4xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold mb-2">{t('viewer.title')}</h1>
            <p className="text-blue-100">{t('viewer.description')}</p>
          </div>
          {onModeChange && (
            <Button 
              onClick={() => onModeChange('generator')} 
              variant="outline" 
              size="sm" 
              className="flex items-center gap-2 border-white hover:bg-white text-blue-950"
            >
              <Map className="h-4 w-4" />
              {t('viewer.switchToGenerator')}
            </Button>
          )}
        </div>
      </div>

      {/* Upload Section */}
      {!hasKML && (
        <div className="p-8 text-center border-b bg-gray-50 py-[16px]">
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('viewer.uploadTitle')}</h3>
          <p className="text-gray-600 mb-4">{t('viewer.uploadDescription')}</p>
          <Button onClick={handleUploadClick} className="bg-blue-600 hover:bg-blue-700">
            <Upload className="h-4 w-4 mr-2" />
            {t('viewer.uploadButton')}
          </Button>
          <input ref={fileInputRef} type="file" accept=".kml" onChange={handleFileUpload} className="hidden" />
        </div>
      )}

      {/* Map Container */}
      <div className="relative">
        <div ref={mapRef} className="w-full h-96 bg-gray-200" style={{ minHeight: '400px' }} />

        {/* File Info */}
        {hasKML && fileName && (
          <div className="absolute top-4 left-4 bg-white border border-gray-300 px-3 py-2 rounded-lg shadow-lg">
            <div className="text-sm text-gray-700">
              <FileText className="h-4 w-4 inline mr-1" />
              {fileName}
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="p-4 bg-gray-50 border-t">
        <div className="flex flex-wrap gap-3 justify-between items-center">
          <div className="text-sm text-gray-600">
            {hasKML ? (
              <span>
                {kmlLayer.current.getLayers().length} {t('viewer.areasLoaded')}
                {totalArea > 0 && (
                  <span className="ml-2 font-medium">
                    • {t('area.total')}: {formatArea(totalArea)}
                  </span>
                )}
              </span>
            ) : (
              t('viewer.noFileLoaded')
            )}
          </div>
          
          <div className="flex gap-3">
            {hasKML && (
              <Button onClick={handleReupload} className="bg-blue-600 hover:bg-blue-700">
                <Upload className="h-4 w-4 mr-2" />
                {t('viewer.uploadAnother')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 bg-gray-100 text-xs text-gray-500 text-center">
        {t('viewer.footerDescription')}
      </div>

      {/* Hidden file input */}
      <input ref={fileInputRef} type="file" accept=".kml" onChange={handleFileUpload} className="hidden" />
    </div>
  );
};

export default KMLViewer;
